'use client'

import { ReactNode, createContext, useMemo, useState } from 'react'
import { ExplorerContextValueObj } from 'types/explorer'
import { TraitType } from '@models/Attributes'
import { useRouter } from 'next/navigation'
import { getQueryString } from '@utils/index'

export const ExplorerContext = createContext<ExplorerContextValueObj>(null)

/**
 * The current query string.
 *
 * This provider sits above every page in the root layout, so it must not call
 * `useSearchParams()`: doing so opts every route out of static rendering and
 * makes Next emit the client-side-rendering error shell instead of prerendered
 * HTML. Every consumer below is an event handler that only ever runs in the
 * browser, so reading `window.location` there is both safe and always current.
 */
const currentSearchParams = () =>
  new URLSearchParams(
    typeof window === 'undefined' ? '' : window.location.search,
  )

export const ExplorerContextProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const router = useRouter()
  const [showFilter, setShowFilter] = useState(false)

  const searchByIdHandler = (value: string) => {
    const query = getQueryString(currentSearchParams(), 'id', value)
    router.push(`/explorer/overview${query}`)
  }

  const selectTraitHandler = (value: TraitType) => {
    router.push(`/explorer/attributes?trait=${value}`)
  }

  const addFilterOption = (option: TraitType, value: string | boolean) => {
    const query = getQueryString(
      currentSearchParams(),
      option,
      value.toString(),
    )
    router.push(`/explorer/overview${query}`)
  }

  const clearFilterOptions = () => {
    router.push('/explorer/overview')
  }

  const handleToggleShowFilter = () => {
    setShowFilter((prevState) => !prevState)
  }

  const selectNFTHandler = (nft_id: string) => {
    const query = getQueryString(currentSearchParams(), 'dewi', nft_id)

    router.push(`/explorer/overview${query}`)
  }

  const exitNFTHandler = () => {
    selectNFTHandler('')
  }

  const value: ExplorerContextValueObj = useMemo(
    () => ({
      showFilter,
      searchByIdHandler,
      selectTraitHandler,
      addFilterOption,
      clearFilterOptions,
      handleToggleShowFilter,
      selectNFTHandler,
      exitNFTHandler,
    }),
    [showFilter],
  )

  return (
    <ExplorerContext.Provider value={value}>
      {children}
    </ExplorerContext.Provider>
  )
}
